import {
  CREATE_PRODUCT_WITH_SKUS_URL,
  GET_SKU_URL,
  LIST_SKUS_URL,
} from '@constants';
import { SKUForm } from '@forms/SKUForm/SKUForm';
import {
  ProductSkusFormValues,
  SelectedSKU,
  SkuServerError,
} from '@forms/SKUForm/types';
import {
  buildCreateProductWithSkusPayload,
  regroupToFormShape,
} from '@forms/SKUForm/payload';
import {
  buildCloneFormValues,
  cloneHeaderFor,
  cloneScopeOf,
} from '@forms/SKUForm/clone';
import { useAuthHttp } from '@hooks';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import { useSetAtom } from 'jotai';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { Button } from '@tailwindComponents/Button';
import {
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
} from '../../store/jotai';
import {
  mapSkuToFormValues,
  mapSkuToProductSeedValues,
  skuPackDescription,
} from '../../utils/sku';

type CreatedSku = {
  inventorySkuId: string;
  skuId?: string | null;
  name: string;
  unitOfMeasure?: string | null;
  amountOfProductInSku?: number | null;
  productDetails?: { productId?: string | null; name?: string | null } | null;
};

type SkuSummary = {
  inventorySkuId: string;
  label: string;
  packDescription: string;
};

const SKUS_PER_CLONE = 50;

const toSkuSummary = (sku: CreatedSku): SkuSummary => ({
  inventorySkuId: sku.inventorySkuId,
  label: sku.skuId || sku.name,
  packDescription: skuPackDescription(sku),
});

const extractSkuServerErrors = (error: any): SkuServerError[] | null => {
  if (error?.response?.status !== 400) return null;
  const errors = error?.response?.data?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  if (!errors.every((entry) => typeof entry?.message === 'string')) return null;
  return errors.map((entry) => ({
    message: entry.message,
    skuIndex: typeof entry.skuIndex === 'number' ? entry.skuIndex : null,
    field: typeof entry.field === 'string' ? entry.field : null,
  }));
};

export function CreateSKU() {
  useDocumentTitle('FLOX - Create Product');
  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const setErrorResponseStatusCode = useSetAtom(errorResponseStatusCodeAtom);
  const setResponseErrorMessages = useSetAtom(responseErrorMessagesAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get('cloneFrom');
  const productFromId = searchParams.get('productFrom');
  const cloneScope = cloneFromId
    ? cloneScopeOf(searchParams.get('cloneScope'))
    : null;
  const sourceSkuId = cloneFromId ?? productFromId;
  const [isFetchingSKU, setIsFetchingSKU] = useState(!!sourceSkuId);
  const [sourceSku, setSourceSku] = useState<SelectedSKU | null>(null);
  const [productSkus, setProductSkus] = useState<SelectedSKU[]>([]);
  const [unclonedSkuCount, setUnclonedSkuCount] = useState(0);
  const [createdSkus, setCreatedSkus] = useState<CreatedSku[]>([]);
  const [skuSummaries, setSkuSummaries] = useState<SkuSummary[]>([]);
  const [serverErrors, setServerErrors] = useState<SkuServerError[] | null>(
    null
  );

  const firstCreatedSku = createdSkus[0] ?? null;

  useEffect(() => {
    setSkuSummaries(createdSkus.map(toSkuSummary));
    const productId = firstCreatedSku?.productDetails?.productId;
    if (!firstCreatedSku || !productId) return undefined;
    let isStale = false;
    (async () => {
      try {
        const { data } = await authHttp.post(LIST_SKUS_URL, {
          productId,
          pagination: { itemsPerPage: 50 },
        });
        if (isStale) return;
        const siblings = ((data?.skus ?? []) as CreatedSku[]).map(toSkuSummary);
        const siblingIds = new Set(
          siblings.map((sibling) => sibling.inventorySkuId)
        );
        const missingCreated = createdSkus
          .filter((created) => !siblingIds.has(created.inventorySkuId))
          .map(toSkuSummary);
        setSkuSummaries([...siblings, ...missingCreated]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    })();
    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdSkus]);

  const resolveHeader = () => {
    if (productFromId) return 'Add SKUs';
    if (cloneScope) return cloneHeaderFor(cloneScope);
    if (cloneFromId) return 'Clone SKU';
    return 'New product';
  };
  const header = resolveHeader();

  const resolveBreadcrumbName = () => {
    if (productFromId) return 'Add SKUs';
    if (cloneScope) return cloneHeaderFor(cloneScope);
    if (cloneFromId) return 'Clone SKU';
    return 'Add product';
  };

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Products', href: '/sku-master', current: false },
    {
      name: resolveBreadcrumbName(),
      href: '/sku/create',
      current: true,
    },
  ];

  const fetchSkuDetails = async (
    inventorySkuId: string
  ): Promise<SelectedSKU | null> => {
    const { data }: AxiosResponse<SelectedSKU> = await authHttp.get(
      GET_SKU_URL,
      { params: { skuId: inventorySkuId } }
    );
    return data?.sku ? data : null;
  };

  const fetchEverySkuOfTheProduct = async (source: SelectedSKU) => {
    const productId = source.sku.productDetails?.productId;
    if (!productId) return;

    const { data } = await authHttp.post(LIST_SKUS_URL, {
      productId,
      pagination: { itemsPerPage: SKUS_PER_CLONE },
    });
    const listedIds = ((data?.skus ?? []) as CreatedSku[]).map(
      (sku) => sku.inventorySkuId
    );
    const siblingIds = listedIds.filter(
      (inventorySkuId) => inventorySkuId !== source.sku.inventorySkuId
    );
    const siblings = await Promise.all(
      siblingIds.map((inventorySkuId) =>
        fetchSkuDetails(inventorySkuId).catch(() => null)
      )
    );
    const cloneable = siblings.filter(
      (sibling): sibling is SelectedSKU => sibling !== null
    );

    setProductSkus([source, ...cloneable]);
    setUnclonedSkuCount(
      Math.max(0, (data?.totalCount ?? listedIds.length) - cloneable.length - 1)
    );
  };

  const fetchSourceSku = async () => {
    if (!sourceSkuId) return;
    setIsFetchingSKU(true);
    try {
      const source = await fetchSkuDetails(sourceSkuId);
      setSourceSku(source);
      if (source && (cloneScope === 'skus' || cloneScope === 'hierarchy')) {
        await fetchEverySkuOfTheProduct(source);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsFetchingSKU(false);
    }
  };

  useEffect(() => {
    setCreatedSkus([]);
    setSourceSku(null);
    setProductSkus([]);
    setUnclonedSkuCount(0);
    setServerErrors(null);
    if (sourceSkuId) {
      fetchSourceSku();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceSkuId, cloneScope]);

  const handleOnSubmit = async (data: ProductSkusFormValues) => {
    setIsSubmitting(true);
    setServerErrors(null);

    const { payload, files } = buildCreateProductWithSkusPayload(data);
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    files.forEach(({ name, file }) => formData.append(name, file, file.name));

    try {
      const { data: created } = await authHttpFormData.post(
        CREATE_PRODUCT_WITH_SKUS_URL,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const skus = (created?.skus ?? []) as CreatedSku[];
      if (skus.length > 0) {
        setCreatedSkus(skus);
      } else {
        navigate('/sku-master', { replace: true });
      }
    } catch (error: any) {
      const structuredErrors = extractSkuServerErrors(error);
      if (structuredErrors) {
        setErrorResponseStatusCode(null);
        setResponseErrorMessages([]);
        setServerErrors(structuredErrors);
      }
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isFetchingSKU) {
    return (
      <div className='flex flex-row justify-center items-center mt-12'>
        <FloxSpinner size='lg' />
      </div>
    );
  }

  if (firstCreatedSku) {
    const productName =
      firstCreatedSku.productDetails?.name ?? firstCreatedSku.name;
    const createdIds = new Set(createdSkus.map((sku) => sku.inventorySkuId));
    const skuNoun = createdSkus.length === 1 ? 'SKU' : 'SKUs';
    const createdCountLabel =
      createdSkus.length === 1 ? 'SKU' : `${createdSkus.length} SKUs`;
    const resolveTitle = () => {
      if (productFromId) return `${createdCountLabel} added`;
      if (createdSkus.length === 1) return 'Product created';
      return `Product created with ${createdSkus.length} SKUs`;
    };
    const title = resolveTitle();
    return (
      <div className='flex justify-center px-8 py-16 font-inter'>
        <div className='flex w-full max-w-md flex-col items-center gap-4 rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm'>
          <CheckCircleIcon className='h-12 w-12 text-green-600' />
          <div>
            <p className='text-lg font-semibold text-gray-900'>{title}</p>
            <p className='mt-1 text-sm text-gray-500'>
              {createdSkus.length === 1 && firstCreatedSku.name !== productName
                ? `${firstCreatedSku.name} is now part of ${productName}.`
                : `${productName} now has ${
                    skuSummaries.length || createdSkus.length
                  } ${skuNoun}.`}
            </p>
          </div>
          {skuSummaries.length > 0 && (
            <div className='self-stretch rounded-lg border border-gray-200 bg-gray-50 p-3 text-left'>
              <p className='text-xs font-medium uppercase tracking-wider text-gray-500'>
                SKUs for {productName}
              </p>
              <ul className='mt-2 flex flex-col gap-1.5'>
                {skuSummaries.map((packFormat) => (
                  <li
                    key={packFormat.inventorySkuId}
                    className='flex items-center justify-between gap-3 text-sm'
                  >
                    <span className='flex items-center gap-2 truncate text-gray-900'>
                      <CheckCircleIcon className='h-4 w-4 shrink-0 text-green-600' />
                      <span className='truncate'>{packFormat.label}</span>
                      {createdIds.has(packFormat.inventorySkuId) && (
                        <span className='shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700'>
                          New
                        </span>
                      )}
                    </span>
                    <span className='shrink-0 text-gray-500'>
                      {packFormat.packDescription}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className='flex flex-col gap-2 self-stretch'>
            <Button
              intent='primary'
              label='Back to Products'
              onClick={() => navigate('/sku-master', { replace: true })}
            />
            <Button
              intent='secondary'
              label={`Add another SKU for ${productName}`}
              onClick={() =>
                navigate(
                  `/sku/create?productFrom=${firstCreatedSku.inventorySkuId}`
                )
              }
            />
          </div>
        </div>
      </div>
    );
  }

  const resolveInitialValues = () => {
    if (!sourceSku) return undefined;
    if (productFromId) {
      return regroupToFormShape(mapSkuToProductSeedValues(sourceSku));
    }
    if (cloneScope) {
      return buildCloneFormValues(sourceSku, productSkus, cloneScope);
    }
    return regroupToFormShape(mapSkuToFormValues(sourceSku, true));
  };
  const initialValues = resolveInitialValues();

  return (
    <SKUForm
      key={`${sourceSkuId ?? 'new'}-${cloneScope ?? ''}`}
      onSubmit={handleOnSubmit}
      isSubmitting={isSubmitting}
      pages={pages}
      header={header}
      initialValues={initialValues}
      lockedProduct={!!productFromId}
      mode='create'
      cloneSourceProductId={
        cloneScope ? sourceSku?.sku.productDetails?.productId ?? '' : null
      }
      unclonedSkuCount={unclonedSkuCount}
      serverErrors={serverErrors}
      onDismissServerErrors={() => setServerErrors(null)}
    />
  );
}
